import { Link } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  BookOpen,
  ShoppingCart,
  Refrigerator,
  Compass,
  Bell,
  LogOut,
  Settings,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const navItems = [
  { path: "/Dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/MealPlan", label: "Meal Plans", icon: Calendar },
  { path: "/MealList", label: "Recipes", icon: BookOpen },
  { path: "/Fridge", label: "Fridge", icon: Refrigerator },
  { path: "/Explore", label: "Explore", icon: Compass },
  { path: "/ShoppingList", label: "Shopping List", icon: ShoppingCart },
  { path: "/Reminders", label: "Reminders", icon: Bell },
];

export function Sidebar({
  isCollapsed,
  setIsCollapsed,
  user,
  onLogout,
  location,
  reminderAlertCount = 0,
}) {
  return (
    <aside
      className={`
        ${isCollapsed ? "w-20" : "w-64"}
        bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out
        fixed md:relative h-screen z-50
        ${isCollapsed ? "-translate-x-full md:translate-x-0" : "translate-x-0"}
      `}
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between min-h-[72-px]">
        {!isCollapsed && (
          <h1 className="text-xl font-bold text-gray-900 truncate ml-2">
            Prepper
          </h1>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`p-2 hover:bg-gray-100 rounded-md transition-colors ${isCollapsed ? "mx-auto" : "ml-auto"}`}
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      {/* Main Nav */}
      <nav className="flex-1 px-3 space-y-1 mt-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <Link
              onClick={() => setIsCollapsed(true)}
              key={item.path}
              to={item.path}
              title={isCollapsed ? item.label : ""}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                ${isCollapsed ? "justify-center" : "justify-start"}
                ${
                  isActive
                    ? "bg-indigo-50 text-indigo-600"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }
              `}
            >
              <span className="relative inline-flex shrink-0">
                <Icon
                  className={isActive ? "text-indigo-600" : "text-gray-400"}
                  size={22}
                />
                {item.path === "/Reminders" && reminderAlertCount > 0 && (
                  <span className="absolute -top-1 -right-1.5 min-w-[14px] h-3.5 px-0.5 rounded-full bg-red-600 text-white text-[10px] font-medium flex items-center justify-center">
                    {reminderAlertCount}
                  </span>
                )}
              </span>
              {!isCollapsed && <span className="truncate">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Bottom Section (Settings, Logout & Profile) */}
      <div className="p-3 border-t border-gray-100 space-y-1">
        <Link
          onClick={() => setIsCollapsed(true)}
          to="/Settings"
          title={isCollapsed ? "Settings" : ""}
          className={`
            flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all
            ${isCollapsed ? "justify-center" : "justify-start"}
            ${
              location.pathname === "/Settings"
                ? "bg-indigo-50 text-indigo-600"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            }
          `}
        >
          <Settings
            size={22}
            className={`shrink-0 ${location.pathname === "/Settings" ? "text-indigo-600" : "text-gray-400"}`}
          />
          {!isCollapsed && <span>Settings</span>}
        </Link>

        <button
          onClick={onLogout}
          title={isCollapsed ? "Logout" : ""}
          className={`
            flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 
            hover:bg-red-50 hover:text-red-600 transition-all
            ${isCollapsed ? "justify-center" : "justify-start"}
          `}
        >
          <LogOut size={22} className="shrink-0" />
          {!isCollapsed && <span>Logout</span>}
        </button>

        <div
          className={`flex items-center gap-3 p-2 mt-2 rounded-lg ${isCollapsed ? "justify-center" : ""}`}
        >
          <div className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 shadow-sm" />
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {user?.username || "User"}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
